import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import AIService from '../../services/AIService';

const NMPLogo = require('../../Images/NMP.png');

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'skybot';
  timestamp: Date;
  audioUrl?: string;
  isLoading?: boolean;
  autoPlay?: boolean; // Nueva propiedad para reproducción automática
  downloadLink?: string; // Nueva propiedad para descarga de Excel
}

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: '¡Hola! Soy SkyBot, tu asistente de IA integrado con Nacional Monte de Piedad. Estoy aquí para ayudarte con información sobre servicios, préstamos, subastas y mucho más. ¿En qué puedo asistirte hoy?',
      sender: 'skybot',
      timestamp: new Date()
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Claude 4 Sonnet toma TODAS las decisiones MCP
  const shouldUseMCP = (text: string): boolean => {
    // Claude 4 Sonnet decide automáticamente qué herramienta usar
    console.log(`🧠 Claude 4 Sonnet will decide MCP tool for: "${text}"`);
    return true; // Siempre usar Claude MCP Decision System
  };

  const handleSendMessage = async (text: string, isVoiceTranscription: boolean = false) => {
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: isVoiceTranscription ? `🎤 "${text}"` : text,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      let response;
      
      // TODAS las consultas van a Claude 4 Sonnet MCP Decision System
      console.log('🧠 Using Claude 4 Sonnet MCP Decision System...');
      response = await AIService.sendMCPVoiceQuery(text);

      if (response.success && response.ai_response) {
        // Create SkyBot message
        // Extract download link from text if present
        let downloadLink = response.download_link;
        let cleanText = response.ai_response;
        
        console.log('🔍 DEBUG: Full response:', response);
        console.log('🔍 DEBUG: ai_response:', response.ai_response);
        console.log('🔍 DEBUG: Contains DOWNLOAD_LINK?', response.ai_response?.includes('[DOWNLOAD_LINK:'));
        
        if (response.ai_response && response.ai_response.includes('[DOWNLOAD_LINK:')) {
          const match = response.ai_response.match(/\[DOWNLOAD_LINK:(.*?)\]/);
          console.log('🔍 DEBUG: Regex match:', match);
          if (match) {
            downloadLink = match[1];
            cleanText = response.ai_response.replace(/\[DOWNLOAD_LINK:.*?\]/g, '').trim();
            console.log('🔗 Extracted download link from text:', downloadLink);
            console.log('🧹 Cleaned text:', cleanText);
          }
        }

        const skybotMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: cleanText,
          sender: 'skybot',
          timestamp: new Date(),
          autoPlay: isVoiceTranscription, // Auto-play only for voice responses
          downloadLink: downloadLink // Add download link if available
        };

        console.log('📝 Final message object:', skybotMessage);
        console.log('🔗 Final downloadLink:', downloadLink);

        // Debug: Log download link
        if (response.download_link) {
          console.log('✅ Download link received:', response.download_link);
          console.log('✅ Download link type:', typeof response.download_link);
        } else {
          console.log('❌ No download link in response');
          console.log('Response data_summary:', response.data_summary);
          console.log('Full response:', response);
        }

        // Add audio if available
        if (response.audio_base64) {
          const audioBlob = base64ToBlob(response.audio_base64, 'audio/mpeg');
          skybotMessage.audioUrl = URL.createObjectURL(audioBlob);
        }

        setMessages(prev => [...prev, skybotMessage]);
        
        // Log processing info
        if (response.endpoint_used) {
          console.log(`🎯 Used endpoint: ${response.endpoint_used}`);
          console.log(`📊 Data summary:`, response.data_summary);
          
          if (response.download_link) {
            console.log(`📥 Excel download available: ${response.download_link}`);
          }
        }
        
        if (response.processing_times) {
          console.log('📊 Processing times:', response.processing_times);
        }
      } else {
        // Error message
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response.message || 'Lo siento, hubo un error al procesar tu mensaje. ¿Podrías intentarlo de nuevo?',
          sender: 'skybot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Disculpa, no pude procesar tu mensaje en este momento. Por favor, inténtalo más tarde.',
        sender: 'skybot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendVoiceText = async (text: string) => {
    // This is for pre-transcribed voice messages
    await handleSendMessage(text, true);
  };

  const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  return (
    <ChatContainer>
      {/* Animated Background Particles */}
      <BackgroundParticles>
        <Particle delay="0s" left="10%" />
        <Particle delay="2s" left="20%" />
        <Particle delay="4s" left="35%" />
        <Particle delay="6s" left="50%" />
        <Particle delay="8s" left="70%" />
        <Particle delay="10s" left="85%" />
        <NeuralLine rotation="15deg" top="20%" delay="0s" />
        <NeuralLine rotation="-10deg" top="60%" delay="2s" />
        <NeuralLine rotation="5deg" top="80%" delay="4s" />
      </BackgroundParticles>

      {/* Main Chat Container */}
      <MainChatContainer>
        {/* Messages Container */}
        <MessagesContainer>
          <AnimatePresence>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
          </AnimatePresence>
          
          {isTyping && (
            <TypingIndicator
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <TypingAvatar>
                <img src={NMPLogo} alt="NMP" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
              </TypingAvatar>
              <TypingContent>
                <TypingDot delay="0s" />
                <TypingDot delay="0.2s" />
                <TypingDot delay="0.4s" />
              </TypingContent>
            </TypingIndicator>
          )}
          
          <div ref={messagesEndRef} />
        </MessagesContainer>
        
        {/* Chat Input */}
        <ChatInput 
          onSendMessage={(text) => handleSendMessage(text, false)} 
          onSendVoiceText={handleSendVoiceText}
          disabled={isTyping} 
        />
      </MainChatContainer>
    </ChatContainer>
  );
};

// Keyframes
const float = keyframes`
  0% { transform: translateY(100vh) rotate(0deg) scale(0.5); opacity: 0; }
  10% { opacity: 1; transform: scale(1); }
  90% { opacity: 1; }
  100% { transform: translateY(-100px) rotate(360deg) scale(0.8); opacity: 0; }
`;

const neuralPulse = keyframes`
  0%, 100% { opacity: 0; transform: scaleX(0); }
  50% { opacity: 1; transform: scaleX(1); }
`;

const logoSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const typing = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-10px); }
`;

// Styled Components
const ChatContainer = styled.div`
  position: relative;
  flex: 1;
  background: linear-gradient(135deg, #98012E 0%, #2c0a0f 100%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const BackgroundParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
`;

const Particle = styled.div<{ delay: string; left: string }>`
  position: absolute;
  width: 80px;
  height: 80px;
  left: ${props => props.left};
  border-radius: 50%;
  background: radial-gradient(circle, rgba(202, 124, 142, 0.3), rgba(152, 1, 46, 0.1));
  animation: ${float} 20s infinite linear;
  animation-delay: ${props => props.delay};
`;

const NeuralLine = styled.div<{ rotation: string; top: string; delay: string }>`
  position: absolute;
  height: 2px;
  width: 100%;
  top: ${props => props.top};
  left: 0;
  background: linear-gradient(90deg, transparent, rgba(202, 124, 142, 0.3), transparent);
  transform: rotate(${props => props.rotation});
  animation: ${neuralPulse} 8s infinite;
  animation-delay: ${props => props.delay};
`;

const MainChatContainer = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(20px);
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  margin: 0;
  height: 100%;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #98012E;
    border-radius: 4px;
  }
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  gap: 12px;
  animation: slideIn 0.5s ease-out;
`;

const TypingAvatar = styled.div`
  width: 55px;
  height: 55px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
  position: relative;
  font-size: 14px;
  background: linear-gradient(135deg, #98012E, #CA7C8E);

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    background: conic-gradient(from 0deg, #98012E, #CA7C8E, #E9C9CE, #98012E);
    animation: ${logoSpin} 3s linear infinite;
    z-index: -1;
  }
`;

const TypingContent = styled.div`
  background: rgba(255, 255, 255, 0.9);
  padding: 15px 20px;
  border-radius: 20px;
  border-bottom-left-radius: 5px;
  display: flex;
  gap: 5px;
  align-items: center;
`;

const TypingDot = styled.div<{ delay: string }>`
  width: 8px;
  height: 8px;
  background: #98012E;
  border-radius: 50%;
  animation: ${typing} 1.4s infinite;
  animation-delay: ${props => props.delay};
`;

export default ChatInterface;
